import { useState } from "react";
import { toast } from "sonner";
import { Heart, Star, Upload, Send, Image as ImageIcon } from "lucide-react";
import { api } from "../../lib/api";
import {
  BrutalButton,
  BrutalCard,
  BrutalLabel,
  BrutalTextarea,
  Badge,
  SectionTitle,
} from "../../components/common/Brutal";

export default function CamperFeedback() {
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState("");
  const [media, setMedia] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMedia({ name: f.name, type: f.type });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!message) return toast.error("Tell us how it went");
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        rating,
        message,
        media_name: media?.name,
        media_type: media?.type,
      });
      toast.success("Mahalo! Feedback received.");
      setSubmitted(true);
      setMessage("");
      setMedia(null);
    } catch {
      toast.error("Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <SectionTitle kicker="Feedback Portal" title="How was the vibe?" />
      {submitted && (
        <BrutalCard className="p-5 bg-[#10B981]/10" data-testid="feedback-success">
          <div className="flex items-center gap-2 text-[#10B981] font-bold">
            <Heart className="h-5 w-5" /> Thanks for sharing!
          </div>
        </BrutalCard>
      )}
      <BrutalCard className="p-6">
        <form onSubmit={submit} className="space-y-5">
          <div>
            <BrutalLabel>Your rating</BrutalLabel>
            <div className="flex gap-2" data-testid="rating-stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setRating(s)}
                  data-testid={`rating-${s}`}
                  className={`h-10 w-10 grid place-items-center border-2 border-[#3D1E6D] transition-all ${
                    s <= rating
                      ? "bg-[#FFB400] shadow-[2px_2px_0_#3D1E6D]"
                      : "bg-white"
                  }`}
                >
                  <Star
                    className={`h-5 w-5 ${
                      s <= rating ? "fill-[#3D1E6D] text-[#3D1E6D]" : "text-[#3D1E6D]/40"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
          <div>
            <BrutalLabel>Your story</BrutalLabel>
            <BrutalTextarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us about your KAOGMA experience..."
              data-testid="feedback-message"
            />
          </div>
          <div>
            <BrutalLabel>Photos / Videos (optional)</BrutalLabel>
            <label
              className="kf-border bg-[#FFF4E6] p-4 flex items-center gap-3 cursor-pointer hover:bg-[#FFB400]/40"
              data-testid="feedback-upload"
            >
              <Upload className="h-5 w-5" strokeWidth={2.75} />
              <span className="font-bold">
                {media ? media.name : "Click to upload (mock)"}
              </span>
              <input
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={handleFile}
              />
              {media && (
                <Badge color="green" className="ml-auto">
                  <ImageIcon className="h-3 w-3" /> Attached
                </Badge>
              )}
            </label>
            <p className="text-xs text-[#6B4E9E] mt-1">
              File simulation only — only filename is stored.
            </p>
          </div>
          <BrutalButton
            type="submit"
            variant="primary"
            className="w-full"
            disabled={submitting}
            data-testid="feedback-submit"
          >
            <Send className="h-4 w-4" />
            {submitting ? "Sending..." : "Send Feedback"}
          </BrutalButton>
        </form>
      </BrutalCard>
    </div>
  );
}
